import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Space,
  Table,
  Typography,
  Modal,
  Tooltip,
} from 'antd';
import moment from 'moment';
import { DeleteOutlined, PlayCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { CampaignRequestorVideoCreativesDialog } from './CampaignRequestorVideoCreativesDialog';
import { Storage } from 'aws-amplify';

const { Text, Title } = Typography;

export const CampaignRequestorVideoCreatives = ({
  form,
  index,
  fieldKey,
  restField,
  name,
  editable = true,
  setPreviousValues,
}) => {
  const campaigns = form.getFieldValue('campaigns');
  const adGroup = campaigns[index]?.adGroup || {};

  // Memoize adGroupCreatives to prevent infinite re-renders
  const adGroupCreatives = useMemo(() => {
    return (adGroup && adGroup.adGroupCreatives) || [];
  }, [adGroup]);
  const [showDialog, setShowDialog] = useState(false);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState('');
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [modalVideos, setModalVideos] = useState([]);
  const [lastProcessedKey, setLastProcessedKey] = useState('');

  const allVideos = useMemo(() => {
    return adGroupCreatives.reduce((acc, item) => acc.concat(item.medias || []), []);
  }, [adGroupCreatives]);

  const handleRemoveCreative = (record) => {
    const newAdGroupCreatives = adGroupCreatives.filter((c) => c.name !== record.name);
    const updatedCampaigns = campaigns.map((campaign, i) => {
      if (i === index) {
        return {
          ...campaign,
          adGroup: {
            ...campaign.adGroup,
            adGroupCreatives: newAdGroupCreatives,
          },
        };
      }
      return campaign;
    });

    form.setFieldsValue({ campaigns: updatedCampaigns });
    setPreviousValues({ ...form.getFieldsValue(), campaigns: updatedCampaigns });
  };

  // Memoize the processing function to prevent infinite loops
  const processAdGroups = useCallback(async () => {
    if (!Array.isArray(adGroupCreatives) || adGroupCreatives.length === 0) {
      if (previewUrls.length > 0) {
        setPreviewUrls([]);
        setLastProcessedKey('');
      }
      return;
    }

    // Create a stable key to check if processing is needed
    const adGroupsKey = adGroupCreatives
      .map((ag) => `${ag.name}-${ag.medias?.length || 0}`)
      .join(',');

    // Skip processing if we've already processed this exact set
    if (adGroupsKey === lastProcessedKey) {
      return;
    }

    const processed = await Promise.all(
      adGroupCreatives.map(async (adGroup) => {
        // Process all videos in the adGroup
        const processedMedias = await Promise.all(
          (adGroup.medias || []).map(async (media) => {
            try {
              const previewUrl = media.key
                ? await Storage.get(media.key, { expires: 600000 })
                : media.url;
              return { ...media, previewUrl };
            } catch (e) {
              console.warn('Failed to get signed URL for:', media.name, e);
              return { ...media, previewUrl: media.url };
            }
          })
        );

        return { ...adGroup, medias: processedMedias };
      })
    );

    setPreviewUrls(processed);
    setLastProcessedKey(adGroupsKey);
  }, [adGroupCreatives, lastProcessedKey, previewUrls.length]);

  // Process adGroupCreatives to get signed URLs for video previews
  useEffect(() => {
    processAdGroups();
  }, [processAdGroups]);

  // Enhanced Video Carousel Modal Component
  const VideoCarouselModal = ({ videos, visible, onClose, initialIndex = 0 }) => {
    const [currentVideoIndex, setCurrentVideoIndex] = useState(initialIndex);

    useEffect(() => {
      setCurrentVideoIndex(initialIndex);
    }, [initialIndex]);

    const currentVideo = videos[currentVideoIndex];

    return (
      <Modal
        open={visible}
        title={`Video Player (${currentVideoIndex + 1} of ${videos.length})`}
        onCancel={onClose}
        footer={null}
        width={900}
        style={{ top: 20 }}
      >
        <div style={{ marginBottom: 16 }}>
          {/* Main Video Player */}
          {currentVideo?.previewUrl && (
            <video
              key={currentVideo.previewUrl} // Force re-render when video changes
              controls
              width="100%"
              style={{ maxHeight: '50vh', backgroundColor: '#000' }}
              autoPlay={false}
            >
              <source src={currentVideo.previewUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          )}

          {/* Video Info */}
          <div style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
            <Text strong>{currentVideo?.name}</Text>
            {currentVideo?.fileType && (
              <Text type="secondary" style={{ marginLeft: 8 }}>
                ({currentVideo.fileType})
              </Text>
            )}
          </div>
        </div>

        {/* Thumbnail Strip */}
        {videos.length > 1 && (
          <div>
            <Text strong style={{ marginBottom: 8, display: 'block' }}>
              Select Video ({videos.length} videos):
            </Text>
            <div
              style={{
                display: 'flex',
                gap: 8,
                overflowX: 'auto',
                padding: '8px 0',
                maxHeight: '120px',
              }}
            >
              {videos.map((video, index) => (
                <div
                  key={index}
                  onClick={() => setCurrentVideoIndex(index)}
                  style={{
                    width: 100,
                    height: 75,
                    cursor: 'pointer',
                    border: currentVideoIndex === index ? '2px solid #1890ff' : '1px solid #d9d9d9',
                    borderRadius: 4,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    position: 'relative',
                    flexShrink: 0,
                  }}
                >
                  {video.previewUrl ? (
                    <>
                      <video
                        src={video.previewUrl}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        muted
                        preload="metadata"
                      />
                      {/* Play icon overlay */}
                      <div
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          fontSize: '16px',
                          color: 'white',
                          textShadow: '0 0 4px rgba(0,0,0,0.8)',
                        }}
                      >
                        {currentVideoIndex === index ? '⏸' : '▶'}
                      </div>
                      {/* Video number badge */}
                      <div
                        style={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          backgroundColor: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          fontSize: '10px',
                          padding: '1px 4px',
                          borderRadius: 2,
                        }}
                      >
                        {index + 1}
                      </div>
                    </>
                  ) : (
                    <Text type="secondary" style={{ fontSize: 10 }}>
                      No Preview
                    </Text>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    );
  };

  // Video Thumbnail Component for preview
  const VideoThumbnail = ({ media, onClick }) => (
    <div
      onClick={onClick}
      style={{
        width: 80,
        height: 60,
        cursor: 'pointer',
        border: '1px solid #d9d9d9',
        borderRadius: 4,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        position: 'relative',
      }}
    >
      {media?.previewUrl ? (
        <>
          <video
            src={media.previewUrl}
            type="video/mp4"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            muted
            preload="metadata"
          />
          {/* Play icon overlay */}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: '20px',
              color: 'white',
              textShadow: '0 0 4px rgba(0,0,0,0.5)',
            }}
          >
            ▶
          </div>
        </>
      ) : (
        <Text type="secondary" style={{ fontSize: 12 }}>
          No Preview
        </Text>
      )}
    </div>
  );

  return (
    <Card title="VIDEO CREATIVES">
      <div className="requester-table-wrapper" style={{ marginTop: 20 }}>
        <div className="requester-table-header">
          <Row>
            <Col span={12}>
              <Title level={5}>Video Creatives</Title>
            </Col>
            {editable && adGroupCreatives.length > 0 ? (
              <Col span={12}>
                <div style={{ textAlign: 'end' }}>
                  <Button
                    type="primary"
                    onClick={() => {
                      setShowDialog(true);
                    }}
                  >
                    Select Video Creatives
                  </Button>
                </div>
              </Col>
            ) : (
              <></>
            )}
          </Row>
        </div>

        {!adGroupCreatives.length ? (
          <div style={{ padding: 20 }}>
            <Space direction="vertical" size="small" align="center" style={{ width: '100%' }}>
              {editable ? (
                <>
                  <Text>Select video creatives from your library to include</Text>
                  <Button type="primary" onClick={() => setShowDialog(true)}>
                    Select Video Creatives
                  </Button>
                </>
              ) : (
                <Text>No Video Creatives Selected</Text>
              )}
            </Space>
          </div>
        ) : (
          <Form.List
            name={[name, 'adGroup', 'adGroupCreatives']}
            fieldKey={[fieldKey, 'adGroup', 'adGroupCreatives']}
          >
            {(adGroupCreativesFields) => (
              <Table
                rowKey={(r, i) => `${r.name}-${i}`}
                scroll={{ x: 800 }}
                columns={[
                  {
                    title: 'Video Creative Name',
                    dataIndex: 'name',
                    key: 'name',
                    ellipsis: true,
                    width: 200,
                    render: (name, record) => (
                      <div>
                        <div style={{ fontWeight: 500 }}>{name}</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {record.medias?.length || 0} video{record.medias?.length !== 1 ? 's' : ''}
                        </Text>
                      </div>
                    ),
                  },
                  {
                    title: 'Click-Through Link',
                    dataIndex: 'clickthrough',
                    key: 'clickthrough',
                    ellipsis: true,
                    width: 200,
                    render: (_, __, i) => (
                      <Form.Item
                        {...adGroupCreativesFields[i]}
                        name={[i, 'clickthrough']}
                        fieldKey={[i, 'clickthrough']}
                        rules={[
                          {
                            required: true,
                            message: 'Click-through URL is required',
                          },
                          {
                            type: 'url',
                            message: 'Please enter a valid URL',
                          },
                        ]}
                      >
                        <Input disabled={!editable} placeholder="https://example.com" />
                      </Form.Item>
                    ),
                  },
                  {
                    title: 'Duration',
                    dataIndex: 'duration',
                    key: 'duration',
                    width: 260,
                    render: (_, __, i) => (
                      <Row gutter={8}>
                        <Col xs={24} sm={12}>
                          <Space
                            direction="vertical"
                            size="small"
                            style={{ width: '100%', minWidth: 120 }}
                          >
                            <Text>Start Date</Text>
                            <Form.Item
                              {...adGroupCreativesFields[i]}
                              name={[i, 'startDate']}
                              fieldKey={[i, 'startDate']}
                            >
                              <DatePicker disabled={!editable} style={{ width: '100%' }} />
                            </Form.Item>
                          </Space>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Space
                            direction="vertical"
                            size="small"
                            style={{ width: '100%', minWidth: 120 }}
                          >
                            <Text>End Date</Text>
                            <Form.Item
                              {...adGroupCreativesFields[i]}
                              name={[i, 'endDate']}
                              fieldKey={[i, 'endDate']}
                            >
                              <DatePicker disabled={!editable} style={{ width: '100%' }} />
                            </Form.Item>
                          </Space>
                        </Col>
                      </Row>
                    ),
                  },
                  {
                    title: 'Preview',
                    dataIndex: 'medias',
                    key: 'preview',
                    width: 200,
                    render: (_, record, i) => {
                      const processedAdGroup = previewUrls[i];

                      if (
                        !processedAdGroup ||
                        !processedAdGroup.medias ||
                        processedAdGroup.medias.length === 0
                      ) {
                        return (
                          <div
                            style={{
                              width: 80,
                              height: 60,
                              border: '1px solid #d9d9d9',
                              borderRadius: 4,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f5f5f5',
                            }}
                          >
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              No Preview
                            </Text>
                          </div>
                        );
                      }

                      // Show multiple video thumbnails like the Gallery column in the dialog
                      const videos = processedAdGroup.medias;

                      return (
                        <Space size="small" wrap>
                          {videos.slice(0, 1).map((media, index) => (
                            <VideoThumbnail
                              key={index}
                              media={media}
                              onClick={() => {
                                // Open carousel modal starting with first video
                                setModalVideos(videos);
                                setSelectedVideoIndex(0);
                                setModalVisible(true);
                              }}
                            />
                          ))}
                          {videos.length > 1 && (
                            <Tooltip title={`View all ${videos.length} videos`}>
                              <div
                                style={{
                                  width: 80,
                                  height: 60,
                                  border: '1px dashed #d9d9d9',
                                  borderRadius: 4,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: '#fafafa',
                                  cursor: 'pointer',
                                }}
                                onClick={() => {
                                  // Open carousel modal with all videos
                                  setModalVideos(videos);
                                  setSelectedVideoIndex(0);
                                  setModalVisible(true);
                                }}
                              >
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  +{videos.length - 1}
                                </Text>
                              </div>
                            </Tooltip>
                          )}
                        </Space>
                      );
                    },
                  },
                  {
                    title: 'Action',
                    key: 'action',
                    width: 80,
                    render: (_, record, i) => {
                      return (
                        <Space size="small">
                          {editable && (
                            <Tooltip title="Remove">
                              <Button
                                size="small"
                                type="text"
                                icon={<DeleteOutlined />}
                                danger
                                onClick={() => handleRemoveCreative(record)}
                              />
                            </Tooltip>
                          )}
                        </Space>
                      );
                    },
                  },
                ]}
                dataSource={adGroupCreatives}
                pagination={false}
                className="requester-table"
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={2}>
                      {adGroupCreatives.length} Video Creative Group(s)
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} colSpan={2}>
                      {allVideos.length} Video Creative(s)
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            )}
          </Form.List>
        )}
      </div>

      {/* Hidden input for form validation parity */}
      <Form.Item
        {...restField}
        name={[name, 'adGroup', 'adGroupCreatives']}
        fieldKey={[fieldKey, 'adGroup', 'adGroupCreatives']}
        hidden
        rules={[
          { required: true, message: `Please select video creatives for ${campaigns[index].name}` },
        ]}
      >
        <Input />
      </Form.Item>

      {(() => {
        const shouldShowModal = showDialog;
        const orgId = form.getFieldValue('advertiserId');
        return (
          shouldShowModal && (
            <CampaignRequestorVideoCreativesDialog
              visible={shouldShowModal}
              setVisible={setShowDialog}
              orgId={orgId}
              selectedCreatives={adGroupCreatives}
              onAddSelections={(selectedAdGroupCreatives) => {
                const updatedCampaigns = campaigns.map((campaign, i) => {
                  if (i === index) {
                    // Get existing adGroupCreatives to preserve clickthrough values
                    const existingAdGroupCreatives = campaign.adGroup?.adGroupCreatives || [];

                    return {
                      ...campaign,
                      adGroup: {
                        ...campaign.adGroup,
                        // Use selected adGroupCreatives directly with proper structure
                        adGroupCreatives: selectedAdGroupCreatives.map((adGroupCreative) => {
                          // Find existing creative with same name to preserve clickthrough
                          const existingCreative = existingAdGroupCreatives.find(
                            existing => existing.name === adGroupCreative.name
                          );

                          return {
                            id: adGroupCreative.id,
                            name: adGroupCreative.name,
                            // Preserve existing clickthrough value, fallback to new value, then empty string
                            clickthrough: existingCreative?.clickthrough || adGroupCreative.clickthrough || '',
                            startDate: existingCreative?.startDate || campaign.adGroup?.startDate
                              ? moment(existingCreative?.startDate || campaign.adGroup.startDate)
                              : moment(form.getFieldValue('startDate')),
                            endDate: existingCreative?.endDate || campaign.adGroup?.endDate
                              ? moment(existingCreative?.endDate || campaign.adGroup.endDate)
                              : moment(form.getFieldValue('endDate')),
                            // Keep all the video medias from the adGroupCreative
                            medias: adGroupCreative.medias || [],
                          };
                        }),
                      },
                    };
                  }
                  return campaign;
                });
                form.setFieldsValue({ campaigns: updatedCampaigns });
                setPreviousValues({ ...form.getFieldsValue(), campaigns: updatedCampaigns });

                // Trigger validation for clickthrough fields
                setTimeout(() => {
                  selectedAdGroupCreatives.forEach((_, idx) => {
                    form.validateFields([
                      ['campaigns', index, 'adGroup', 'adGroupCreatives', idx, 'clickthrough']
                    ]).catch(() => {});
                  });
                }, 0);
              }}
            />
          )
        );
      })()}
      <VideoCarouselModal
        videos={modalVideos}
        visible={modalVisible}
        onClose={() => {
          setModalVisible(false);
          setModalVideos([]);
          setSelectedVideoIndex(0);
        }}
        initialIndex={selectedVideoIndex}
      />
    </Card>
  );
};

export default CampaignRequestorVideoCreatives;
